import { defineStore } from "pinia";
import { ExaminationSettings } from "./entity";
import { ExaminationSettingsList } from "./list.gen";
import type {
  ExaminationSettingsStore,
  ExaminationSettingsStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const examinationSettingsList = new ExaminationSettingsList();
export const useExaminationSettingsStore = defineStore(
  "examinationSettingsStore",
  {
    state: (): ExaminationSettingsStoreState => ({
      activeID: null,
      cache: {},
      list: [] as ExaminationSettings[],
      retrievalTimestamp: 0,
      updateKey: 0,
      storeUpdateTimeout: null,
    }),
    actions: {
      initializeStore(): void {
        this.convertList();
      },
      emitFormUpdateEvent(id: string): void {
        window.dispatchEvent(
          new CustomEvent("form-update-ExaminationSettings", {
            detail: { id },
          }),
        );
      },
      emitStoreUpdateEvent(): void {
        // Clear existing timeout
        if (this.storeUpdateTimeout !== null) {
          clearTimeout(this.storeUpdateTimeout);
        }

        // Set new timeout
        this.storeUpdateTimeout = setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("store.update.ExaminationSettings"),
          );
          this.storeUpdateTimeout = null;
        }, 1500);
      },
      async activate(examinationSettings: ExaminationSettings): Promise<void> {
        // Set active
        await examinationSettings
          .activate(auth.firebaseUser?.uid)
          .then(() => {
            this.activeID = examinationSettings.id;
          })
          .catch((error) => {
            console.error(error);
            this.activeID = null;
          });
      },
      add(examinationSettings: ExaminationSettings): void {
        const id = examinationSettings.id;
        if (typeof id === "string" && !this.contains(id)) {
          this.list.push(examinationSettings);

          // Set active
          if (this.active === null) {
            this.activeID = examinationSettings.id;
          }
        } else {
          this.reinsert(examinationSettings);
        }
        this.updateKey += 1;
      },
      convertList(): void {
        this.list = (this.list as ExaminationSettings[]).map((x: any) => {
          const examinationSettings = new ExaminationSettings({});
          examinationSettings.set(x as JSONData);
          return examinationSettings;
        });
      },
      async delete(examinationSettings: ExaminationSettings): Promise<void> {
        await examinationSettings
          .delete()
          .then(() => {
            this.remove(examinationSettings);
          })
          .finally(() => {
            this.emitStoreUpdateEvent();
          });
      },
      async deleteById(id: string): Promise<void> {
        const examinationSettings = (this.list as ExaminationSettings[]).find(
          (x: ExaminationSettings) => x.id === id,
        );
        if (examinationSettings !== undefined) {
          await this.delete(examinationSettings);
        } else {
          throw new Error("Could not find ExaminationSettings with id: " + id);
        }
      },
      contains(id: string): boolean {
        return (this.list as ExaminationSettings[]).some(
          (x: ExaminationSettings) => x.id === id,
        );
      },
      get(id: string): ExaminationSettings | null {
        return (
          (this.list as ExaminationSettings[]).find(
            (x: ExaminationSettings) => x.id === id,
          ) || null
        );
      },
      reinsert(examinationSettings: ExaminationSettings): void {
        const index = (this.list as ExaminationSettings[]).findIndex(
          (x: ExaminationSettings) => x.id === examinationSettings.id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            examinationSettings,
            ...this.list.slice(index + 1),
          ];
        } else {
          this.add(examinationSettings);
        }
        this.updateKey += 1;
      },
      remove({ id }: ExaminationSettings): void {
        const index = (this.list as ExaminationSettings[]).findIndex(
          (x: ExaminationSettings) => x.id === id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            ...this.list.slice(index + 1),
          ];
        }
        this.updateKey += 1;
      },
      async retrieve(id: string, force = false): Promise<ExaminationSettings> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          const examinationSettings = this.get(id);
          if (examinationSettings !== null) {
            return examinationSettings;
          }
        }

        // Retrieve examinationSettings
        const examinationSettings = new ExaminationSettings({});

        // Get examinationSettings
        await examinationSettings.get([id]).then(async (entity) => {
          this.reinsert(entity);

          // Activate examinationSettings
          await this.activate(entity);
        });

        return examinationSettings;
      },
      async retrieveAll(force = false): Promise<{
        active: string | null;
        items: ExaminationSettings[];
      }> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          return {
            active: this.activeID,
            items: this.list as ExaminationSettings[],
          };
        }

        // Clear list
        while (this.list.length > 0) {
          this.list.pop();
        }

        // Retrieve list
        await examinationSettingsList.getAll().then(async (list) => {
          list.forEach((examinationSettings) => {
            this.reinsert(examinationSettings);
          });

          // Activate examinationSettings
          if (list.length > 0) {
            if (examinationSettingsList.active !== null) {
              this.activeID = examinationSettingsList.active;
            } else {
              await this.activate(list[0]);
            }
          }
        });

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        // Set user property
        if (this.activeID !== null) {
          analytics.setUserProperty("examination_settings_id", this.activeID);
        }

        return {
          active: this.activeID,
          items: this.list as ExaminationSettings[],
        };
      },
      async retrievePaginated(
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: ExaminationSettings[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as ExaminationSettings,
            ),
          };
        }

        // Retrieve list
        const items = await examinationSettingsList
          .getPaginated([], pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((examinationSettings) => {
              this.reinsert(examinationSettings);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: ExaminationSettings) => x.id),
          pagination: newPagination,
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items,
        };
      },
      async query(
        queries: Query[],
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: ExaminationSettings[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as ExaminationSettings,
            ),
          };
        }

        // Retrieve list
        const items = await examinationSettingsList
          .query(queries, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((examinationSettings) => {
              this.reinsert(examinationSettings);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: ExaminationSettings) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items: items,
        };
      },
      async search(
        query: string,
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: ExaminationSettings[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey =
          query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            items: cached.ids.map(
              (id: string) => this.get(id) as ExaminationSettings,
            ),
            pagination: cached.pagination.deepCopy(),
          };
        }

        // Retrieve list
        const items = await examinationSettingsList
          .search(query, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((examinationSettings) => {
              this.reinsert(examinationSettings);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: ExaminationSettings) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          items: items,
          pagination: newPagination,
        };
      },
      async update(examinationSettings: ExaminationSettings): Promise<string> {
        // Update examinationSettings
        await examinationSettings.update();

        // Reinsert examinationSettings
        this.reinsert(examinationSettings);

        // Activate examinationSettings
        if (this.active === null) {
          await this.activate(examinationSettings);
        }

        this.emitStoreUpdateEvent();

        return examinationSettings.id;
      },
    },
    getters: {
      active(): ExaminationSettings | null {
        if (this.activeID === null) {
          return null;
        }

        const examinationSettings = (this.list as ExaminationSettings[]).find(
          (x: ExaminationSettings) => x.id === this.activeID,
        );
        if (examinationSettings === undefined) {
          return null;
        }

        return examinationSettings;
      },
      refreshRequired(): boolean {
        return (
          this.retrievalTimestamp === 0 ||
          this.retrievalTimestamp < Date.now() - 30 * 60000 ||
          this.list.length === 0
        );
      },
    },
  },
);

// Export a function to create a new instance of the store
export function createExaminationSettingsStore(): ExaminationSettingsStore {
  const store = useExaminationSettingsStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as ExaminationSettingsStore; // TODO: Fix this
}
